import { createAudio } from "./audio/audio";
import { createInput, type InputState } from "./input/input";
import { loadWorld, saveWorld } from "./persist/save";
import { loadAtlas, type Atlas } from "./render/assets";
import { createCam, tickCam } from "./render/camera";
import { drawWorld } from "./render/draw";
import { drawHud } from "./render/hud";
import { ensureSystems } from "./sim/events";
import { DT, type WorldState } from "./sim/types";
import { createWorld, tickWorld } from "./sim/world";

const IDLE: InputState = {
  ax: 0,
  ay: 0,
  dash: false,
  strike: false,
  interact: false,
  lantern: false,
  pause: false,
  confirm: false,
};

/**
 * What `window.__CINDERWELL` promises a test harness.
 *
 * The game loop is driven by `requestAnimationFrame`, which does not fire in a
 * hidden or throttled tab — a headless lab that navigates and screenshots is
 * photographing a still world unless it drives the sim itself. That freeze is
 * correct for a singleplayer game and is deliberately left alone; `step()` is
 * the way to advance it on purpose.
 */
export interface QaHandle {
  /** Always the live world. Never a snapshot — `reset()` must not strand it. */
  readonly world: WorldState;
  readonly ready: boolean;
  /** Advance the sim by `n` fixed ticks and redraw. Independent of rAF. */
  step(n?: number, over?: Partial<InputState>): WorldState;
  reset(): WorldState;
  save(): void;
}

export async function boot(canvas: HTMLCanvasElement): Promise<void> {
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) throw new Error("canvas");
  const input = createInput(canvas);
  const audio = createAudio();
  const cam = createCam();
  const atlas: Atlas = await loadAtlas();
  const boot = new URLSearchParams(location.search);
  const qa = boot.has("fresh") || boot.has("wake");
  let world = qa ? createWorld() : (loadWorld() ?? createWorld());
  if (boot.has("wake")) world.mode = "overworld";
  // loadWorld() already does this; createWorld() does not. Symmetry so the
  // cross-system layer is present before the first tick either way.
  ensureSystems(world);

  const placeCam = () => {
    cam.x = world.player.x + world.player.facing.x * 2.1;
    cam.y = world.player.y + world.player.facing.y * 2.1;
  };
  placeCam();

  let acc = 0;
  let last = performance.now();
  let saveT = 0;
  let lastStrike = world.player.strikePhase;

  /**
   * A hidden or unsized embedding reports 0 for every dimension — I measured
   * clientWidth, innerWidth and the bounding rect all at 0 in a backgrounded
   * tab. That silently produces a 0x0 canvas, so the draw calls succeed and the
   * screenshot is blank. Keep a floor so there is always a real frame to look at.
   */
  const view = () => ({
    vw: canvas.clientWidth || window.innerWidth || 960,
    vh: canvas.clientHeight || window.innerHeight || 540,
  });

  const resize = () => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const { vw, vh } = view();
    canvas.width = Math.floor(vw * dpr);
    canvas.height = Math.floor(vh * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();
  window.addEventListener("resize", resize);
  canvas.addEventListener("pointerdown", () => {
    canvas.focus();
    audio.resume();
  });

  const render = () => {
    const { vw, vh } = view();
    ctx.imageSmoothingEnabled = false;
    drawWorld(ctx, world, atlas, cam, vw, vh);
    drawHud(ctx, world, atlas, vw, vh);
  };

  const loop = (now: number) => {
    const raw = Math.min(0.05, (now - last) / 1000);
    last = now;
    acc += raw;
    const sample = input.sample();
    let steps = 0;
    while (acc >= DT && steps < 5) {
      tickWorld(world, DT, sample);
      acc -= DT;
      steps += 1;
    }
    const { vw, vh } = view();
    tickCam(cam, world, raw, vw, vh);
    if (world.player.strikePhase === "active" && lastStrike !== "active") {
      audio.swing();
      if (world.player.strikeLanded) audio.hit();
    }
    lastStrike = world.player.strikePhase;
    if (Math.hypot(world.player.vx, world.player.vy) > 1.2) audio.foot(world.weather === "rain");
    render();

    saveT += raw;
    if (saveT > 8 && world.mode !== "title" && !qa) {
      saveT = 0;
      saveWorld(world);
    }
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  const handle: QaHandle = {
    // Getter, not a captured value: `reset()` rebinds `world`, and a snapshot
    // property would leave a harness holding the dead one while the renderer
    // moved on.
    get world() {
      return world;
    },
    get ready() {
      return true;
    },
    step(n = 1, over?: Partial<InputState>) {
      const sample: InputState = over ? { ...IDLE, ...over } : IDLE;
      for (let i = 0; i < n; i++) tickWorld(world, DT, sample);
      const { vw, vh } = view();
      tickCam(cam, world, n * DT, vw, vh);
      render();
      return world;
    },
    reset() {
      world = createWorld();
      // Reproduce boot, not half of it. Without the `wake` flag the world comes
      // back in title mode, where tickWorld early-returns — so a harness that
      // reset and stepped would watch a world that never moves.
      if (boot.has("wake")) world.mode = "overworld";
      ensureSystems(world);
      placeCam();
      acc = 0;
      saveT = 0;
      lastStrike = world.player.strikePhase;
      render();
      return world;
    },
    save() {
      saveWorld(world);
    },
  };

  (window as unknown as { __CINDERWELL: QaHandle }).__CINDERWELL = handle;
  // `boot()` awaits the atlas, so a lab that navigates and asserts immediately
  // would otherwise race asset load. Poll `__CINDERWELL?.ready` or await this.
  window.dispatchEvent(new CustomEvent("cinderwell:ready"));
}
